import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, Clock3, Download, LoaderCircle, RefreshCw, XCircle } from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';

const labels = {
  queued: 'Queued', running: 'In progress', cancelling: 'Cancelling', completed: 'Completed',
  partial_completed: 'Partially completed', failed: 'Needs attention', cancelled: 'Cancelled',
};

const jobTitles = {
  test_pre_analysis: 'Test case planning',
  test_case_generation: 'Test case generation',
  test_case_regeneration: 'Test case regeneration',
  document_extraction: 'Document extraction',
  manual_input_ingestion: 'Text requirement processing',
  playwright_script_generation: 'Playwright script generation',
  playwright_execution: 'Browser test run',
};

export const titleFor = (job) => job?.result_summary?.title || jobTitles[job?.type]
  || String(job?.type || 'Background job').replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const elapsed = (job, now) => {
  const start = new Date(job.started_at || job.created_at || 0).getTime();
  const end = job.completed_at ? new Date(job.completed_at).getTime() : now;
  if (!start || Number.isNaN(start)) return '';
  const seconds = Math.max(0, Math.floor((end - start) / 1000));
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

export const JobStatusBadge = ({ status }) => {
  const Icon = status === 'completed' ? CheckCircle2
    : status === 'failed' || status === 'partial_completed' ? AlertTriangle
      : status === 'cancelled' ? XCircle : status === 'queued' ? Clock3 : LoaderCircle;
  return <span className={`job-status-badge is-${status}`}><Icon aria-hidden="true" />{labels[status] || status}</span>;
};

export const JobProgressCard = ({ job, items = [], onCancel, onRetry, onOpen, onRemove, compact = false }) => {
  const [now, setNow] = useState(Date.now());
  const [expanded, setExpanded] = useState(false);
  const active = ['queued', 'running', 'cancelling'].includes(job.status);
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  const counts = useMemo(() => items.reduce((result, item) => {
    const status = String(item.status || 'queued');
    result[status] = (result[status] || 0) + 1;
    return result;
  }, {}), [items]);
  const error = job.error?.message || (typeof job.error === 'string' ? job.error : '') || job.actionError;
  const canRetry = ['failed', 'partial_completed', 'cancelled'].includes(job.status) && job.adapter !== 'bulk';
  const canDownload = job.adapter !== 'bulk'
    && job.status === 'completed'
    && Boolean(job.result_summary?.download_ready);
  const stage = job.progress?.message || String(job.progress?.stage || 'Waiting').replace(/[_-]+/g, ' ');
  const currentItem = items.find((item) => ['running', 'processing', 'in_progress'].includes(String(item.status || '').toLowerCase())) || items.find((item) => item.status === 'queued');
  const itemLabel = currentItem?.section_title || (currentItem?.section_number ? `Section ${currentItem.section_number}` : currentItem?.name || currentItem?.label || '');

  return (
    <article className={`job-progress-card ${compact ? 'is-compact' : ''}`} aria-labelledby={`job-title-${job.id}`}>
      <div className="job-progress-card-head">
        <div className="min-w-0">
          <h3 id={`job-title-${job.id}`}>{titleFor(job)}</h3>
          <p>{stage}</p>
        </div>
        <JobStatusBadge status={job.status} />
      </div>
      {!compact && job.source_id && <p className="job-progress-context">{job.module === 'brd' ? 'BRD workflow' : job.module || 'Source'} · {itemLabel || (items.length ? `${items.length} ${items.length === 1 ? 'task' : 'tasks'}` : 'Background task')}</p>}
      <div
        className="job-progress-track"
        role="progressbar"
        aria-label={`${titleFor(job)} progress`}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={job.progress?.percent || 0}
        aria-valuetext={`${job.progress?.percent || 0}% — ${stage}`}
      >
        <span style={{ width: `${job.progress?.percent || 0}%` }} />
      </div>
      <div className="job-progress-meta">
        <span>{job.progress?.percent || 0}%</span>
        {job.progress?.total > 0 && <span>{job.progress.current} of {job.progress.total} items</span>}
        <span>{elapsed(job, now)}</span>
      </div>
      {items.length > 0 && !compact && (
        <p className="job-progress-counts">
          {counts.completed || 0} completed · {counts.failed || 0} failed · {(counts.running || 0) + (counts.queued || 0)} remaining
        </p>
      )}
      {error && !compact && (
        <div className="job-progress-error">
          <button type="button" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
            <ChevronDown aria-hidden="true" /> Failure details
          </button>
          {expanded && <p>{error}</p>}
        </div>
      )}
      {!compact && (
        <div className="job-progress-actions">
          {onOpen && <button type="button" onClick={() => onOpen(job)}>Open workflow</button>}
          {canDownload && <a className="is-primary" href={`${API_BASE_URL}${API_ENDPOINTS.JOB_DOWNLOAD(job.id)}`} target="_blank" rel="noreferrer"><Download aria-hidden="true" />Download</a>}
          {active && <button type="button" onClick={() => onCancel?.(job)} disabled={job.actionPending || job.status === 'cancelling'}>Cancel</button>}
          {canRetry && <button type="button" className="is-primary" onClick={() => onRetry?.(job)} disabled={job.actionPending}><RefreshCw aria-hidden="true" />{job.status === 'partial_completed' ? 'Retry failed items' : 'Retry'}</button>}
          {onRemove && <button type="button" onClick={onRemove} aria-label={`Remove ${titleFor(job)} from background jobs`}>Remove</button>}
        </div>
      )}
    </article>
  );
};
